import type { Player, PoseFrame, Poses } from "../data/types";

/**
 * Map a wall-clock time onto a pose, **linearly interpolated** between the two
 * bracketing pose frames so playback is smooth at any render rate regardless of
 * the (lower) capture rate. Pose frames arrive at the source video rate (e.g.
 * 25 fps) while the rAF loop renders at the display rate (e.g. 60 fps); without
 * interpolation the skeleton freezes then jumps each capture frame, which reads
 * as choppy — especially with the dense 133-keypoint whole-body skeleton.
 *
 * Cached-index linear scan for forward play (O(1) typical), binary-search
 * fallback for seek/scrub (O(log n)). The caller passes a time in seconds
 * **already aligned to the pose timeline** (i.e. video.currentTime - ts0).
 */
export class PoseInterpolator {
  private frames: PoseFrame[];
  private lastIdx = 0;

  /** Last frame ts (seconds, aligned). Useful for the timeline UI's max. */
  readonly duration: number;
  /** First frame ts (always 0 after the exporter subtracts ts0... but keep). */
  readonly start: number;

  constructor(poses: Poses) {
    this.frames = poses.frames;
    this.start = poses.frames[0]?.ts ?? 0;
    this.duration = (poses.frames.at(-1)?.ts ?? 0) - this.start;
  }

  frameAt(t: number): PoseFrame {
    const frames = this.frames;
    if (!frames.length) {
      return { ts: 0, players: [] };
    }
    const absT = t + this.start;

    // Fast path: forward play — most ticks advance by < 1 frame.
    let i = this.lastIdx;
    while (i + 1 < frames.length && frames[i + 1].ts <= absT) i++;
    while (i > 0 && frames[i].ts > absT) i--;

    // If we moved more than a few frames, just binary-search.
    if (Math.abs(i - this.lastIdx) > 4) {
      i = this.binarySearch(absT);
    }
    this.lastIdx = i;

    // Interpolate between frame i (<= absT) and i+1 (> absT).
    const a = frames[i];
    const b = frames[i + 1];
    if (!b) return a;                      // past the last sample: hold
    const span = b.ts - a.ts;
    if (span <= 0) return a;
    const alpha = (absT - a.ts) / span;
    if (alpha <= 0) return a;
    if (alpha >= 1) return b;
    return PoseInterpolator.lerpFrame(a, b, alpha);
  }

  /** Linearly blend two pose frames. Players present in both are interpolated by
   *  gid; players in only one frame pass through unblended (so they appear/leave
   *  cleanly rather than popping from the origin). */
  private static lerpFrame(a: PoseFrame, b: PoseFrame, t: number): PoseFrame {
    const byGid = new Map<number, Player>();
    for (const p of b.players) byGid.set(p.gid, p);
    const players: Player[] = [];
    const seen = new Set<number>();
    for (const pa of a.players) {
      const pb = byGid.get(pa.gid);
      seen.add(pa.gid);
      if (!pb || pb.kpts.length !== pa.kpts.length) {
        players.push(pa);                  // only in A (or shape mismatch): hold A
        continue;
      }
      const n = pa.kpts.length;
      const kpts = new Array<number>(n);
      for (let j = 0; j < n; j++) kpts[j] = pa.kpts[j] + (pb.kpts[j] - pa.kpts[j]) * t;
      // Scores: take the max so a joint confident in either frame stays drawn.
      const m = pa.scores.length;
      const scores = new Array<number>(m);
      for (let j = 0; j < m; j++) scores[j] = Math.max(pa.scores[j], pb.scores[j]);
      players.push({ gid: pa.gid, kpts, scores });
    }
    for (const pb of b.players) if (!seen.has(pb.gid)) players.push(pb);  // only in B
    return { ts: a.ts + (b.ts - a.ts) * t, players };
  }

  private binarySearch(absT: number): number {
    const frames = this.frames;
    let lo = 0;
    let hi = frames.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >>> 1;
      if (frames[mid].ts <= absT) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }
}
